/**
 * monetization/providers — abstracción provider-agnóstica de pagos (lado cliente).
 *
 * El cliente NUNCA habla con la pasarela directamente: el server expone
 * POST /api/billing/checkout y el provider inyectado allí decide (trait Rust
 * `PaymentProvider`). Este espejo TS describe cómo la UI debe INTERPRETAR el
 * provider activo: si ofrece checkout automático o si el upgrade es un flujo
 * manual (provider "none": lo aplica un admin).
 *
 * Añadir un proveedor nuevo = registrar su descriptor aquí + implementar el
 * trait en Rust. Ninguna pantalla ni lógica de dominio cambia.
 */

export interface CheckoutResult {
  provider: string;
  /** URL de pago hosted a la que redirigir (si el provider la ofrece). */
  checkoutUrl: string | null;
  /** true ⇒ upgrade manual (contactar / lo aplica un admin). */
  manual: boolean;
}

/** Descriptor de un proveedor de pagos, visto desde la UI. */
export interface PaymentProviderDescriptor {
  id: string;
  /** ¿El upgrade abre un checkout hosted? (false ⇒ flujo manual). */
  supportsCheckout: boolean;
  /** ¿El server recibe webhooks de este provider? */
  supportsWebhooks: boolean;
}

const NONE_PROVIDER: PaymentProviderDescriptor = {
  id: "none",
  supportsCheckout: false,
  supportsWebhooks: false,
};

const REGISTRY: Record<string, PaymentProviderDescriptor> = {
  none: NONE_PROVIDER,
  stripe: { id: "stripe", supportsCheckout: true, supportsWebhooks: true },
};

/** Ids registrados (para el Select del panel admin). */
export const PAYMENT_PROVIDER_IDS = Object.keys(REGISTRY);

/** Descriptor del provider activo; desconocido ⇒ se comporta como "none". */
export function getPaymentProvider(id: string | undefined): PaymentProviderDescriptor {
  return REGISTRY[id ?? "none"] ?? NONE_PROVIDER;
}

/**
 * Interpreta la respuesta del checkout del server de forma provider-agnóstica:
 * devuelve la acción que la UI debe ejecutar.
 */
export type CheckoutAction = { kind: "redirect"; url: string } | { kind: "manual" };

export function interpretCheckout(result: CheckoutResult): CheckoutAction {
  if (!result.manual && result.checkoutUrl) {
    return { kind: "redirect", url: result.checkoutUrl };
  }
  return { kind: "manual" };
}
